import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Label, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";
import AdminService from "../../services/admin";
import Breadcrumbs from "../../components/Breadcrumb";

const SpecialDaysConfig = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: t("ADMIN.TITLE"), link: "#" },
    { title: t("ADMIN.SPECIAL_DAYS_CONFIG"), link: "#" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState({
    Day01: 1,
    Day10: 10,
    Day15: 15,
    Day28: 23,
  });

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const response = await AdminService.getSpecialDaysConfig();
      if (response.data && response.data.status === 200 && response.data.data) {
        setConfig({
          Day01: response.data.data.Day01 || response.data.data.day01 || 1,
          Day10: response.data.data.Day10 || response.data.data.day10 || 10,
          Day15: response.data.data.Day15 || response.data.data.day15 || 15,
          Day28: response.data.data.Day28 || response.data.data.day28 || 23,
        });
      }
    } catch (error) {
      console.error("Error loading config:", error);
      toast.error(t("ADMIN.LOAD_ERROR"));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 1 && numValue <= 31) {
      setConfig((prev) => ({ ...prev, [field]: numValue }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    dispatch(setLoadingOverlay(true));
    try {
      const response = await AdminService.updateSpecialDaysConfig(config);
      if (response.data && response.data.status === 200) {
        toast.success(t("ADMIN.SAVE_SUCCESS"));
      } else {
        toast.error(response.data?.message || t("ADMIN.SAVE_ERROR"));
      }
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error(t("ADMIN.SAVE_ERROR"));
    } finally {
      setIsSaving(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs
          title={t("ADMIN.SPECIAL_DAYS_CONFIG")}
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            <h4 className="card-title mb-4">{t("ADMIN.SPECIAL_DAYS_CONFIG")}</h4>

            <p className="text-muted mb-4">
              {t("ADMIN.SPECIAL_DAYS_DESCRIPTION")}
            </p>

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              <Row>
                <Col md={6} lg={3}>
                  <div className="mb-3">
                    <Label htmlFor="day01">{t("ADMIN.DAY_01_LABEL")}</Label>
                    <Input
                      type="number"
                      id="day01"
                      min="1"
                      max="31"
                      value={config.Day01}
                      onChange={(e) => handleChange("Day01", e.target.value)}
                      className="form-control"
                    />
                  </div>
                </Col>

                <Col md={6} lg={3}>
                  <div className="mb-3">
                    <Label htmlFor="day10">{t("ADMIN.DAY_10_LABEL")}</Label>
                    <Input
                      type="number"
                      id="day10"
                      min="1"
                      max="31"
                      value={config.Day10}
                      onChange={(e) => handleChange("Day10", e.target.value)}
                      className="form-control"
                    />
                  </div>
                </Col>

                <Col md={6} lg={3}>
                  <div className="mb-3">
                    <Label htmlFor="day15">{t("ADMIN.DAY_15_LABEL")}</Label>
                    <Input
                      type="number"
                      id="day15"
                      min="1"
                      max="31"
                      value={config.Day15}
                      onChange={(e) => handleChange("Day15", e.target.value)}
                      className="form-control"
                    />
                  </div>
                </Col>

                <Col md={6} lg={3}>
                  <div className="mb-3">
                    <Label htmlFor="day28">{t("ADMIN.DAY_28_LABEL")}</Label>
                    <Input
                      type="number"
                      id="day28"
                      min="1"
                      max="31"
                      value={config.Day28}
                      onChange={(e) => handleChange("Day28", e.target.value)}
                      className="form-control"
                    />
                  </div>
                </Col>
              </Row>
            )}

            <div className="mt-4">
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isSaving || isLoading}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("SAVING")}
                  </>
                ) : (
                  t("SAVE_TITLE")
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default SpecialDaysConfig;